import moment from "moment-timezone";
import { TranslationService } from "@/core/services/TranslationService";

export const FormatMethods: any = {
  Date(value: string, format = "", utcDateRequired = true): string {
    const displayFormat =
      format || process.env.VUE_APP_DISPLAY_DATE || "MM-DD-YYYY";
    if (!moment(value).isValid()) {
      return value;
    }
    if (utcDateRequired) {
      return moment(value).utc().format(displayFormat);
    }
    return moment(value).format(displayFormat);
  },
  DateWithoutUtc(value: string, format = "", utcDateRequired = false): string {
    const displayFormat =
      format || process.env.VUE_APP_DISPLAY_DATE || "MM-DD-YYYY";
    if (!moment(value).isValid()) {
      return value;
    }
    if (utcDateRequired) {
      return moment(value).utc().format(displayFormat);
    }
    return moment(value).format(displayFormat);
  },

  DateAndTime(value: string, format = ""): string {
    if (!moment(value).isValid()) {
      return value;
    }
    return moment(value).format("MM-DD-YYYY [at] h:mm a");
  },
  DateTime(value: string, format = ""): string {
    const displayFormat =
      format || process.env.VUE_APP_DISPLAY_TIME || "MM-DD-YYYY h:mm a";
    if (!moment(value).isValid()) {
      return value;
    }
    return moment(value).format(displayFormat);
  },

  DateAndTimeInUtc(value: string, format = "MM/DD/YYYY [at] h:mm a"): string {
    if (!moment(value).isValid()) {
      return value;
    }
    return moment.utc(value).format(format);
  },

  DateTimeRange({ start, end }: any): string {
    const startDate = moment(start);
    const endDate = moment(end);

    // Same day; display times,
    if (endDate.diff(startDate, "d") < 1 && startDate.isSame(startDate, "d")) {
      return `${startDate.format("h:mma")}–${endDate.format("h:mma")}`;
    }

    // Within 24hrs but not same date; display date and time.
    if (endDate.diff(startDate, "d") < 1) {
      return `${startDate.format("MM/DD/YYYY h:mma")}–${endDate.format(
        "MM/DD/YYYY h:mma"
      )}`;
    }

    // Spans multiple day; display dates.
    return `${startDate.format("MM/DD/YYYY")}–${endDate.format("MM/DD/YYYY")}`;
  },
  convertUTCToLocal(str) {
    // Regular expression to match the date format in the string
    if (str.includes("end date has been updated from")) {
      return str;
    }
    if (str.includes("end date has been added to")) {
      return str;
    }
    const regex = /"\d{2}-\d{2}-\d{4} \d{1,2}:\d{2} [AP]M"/g;

    // Function to convert UTC timestamp to local timestamp
    function toLocalTime(utcDateString) {
      // Remove the enclosing quotes
      utcDateString = utcDateString.replace(/"/g, "");

      // Parse the UTC date string into its components
      const [datePart, timePart, period] = utcDateString.split(" ");
      const [month, day, year] = datePart.split("-");
      // eslint-disable-next-line prefer-const
      let [hours, minutes] = timePart.split(":");

      // Convert hours to 24-hour format
      hours = parseInt(hours, 10);
      if (period === "PM" && hours < 12) {
        hours += 12;
      }
      if (period === "AM" && hours === 12) {
        hours = 0;
      }

      // Create a Date object in UTC
      const utcDate = new Date(
        Date.UTC(
          parseInt(year),
          parseInt(month) - 1,
          parseInt(day),
          hours,
          parseInt(minutes)
        )
      );

      // Format the local date and time
      const options: Intl.DateTimeFormatOptions = {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      };

      const localDateString = utcDate.toLocaleString("en-US", options);

      // Convert format from MM/DD/YYYY, HH:MM AM/PM to MM-DD-YYYY HH:MM AM/PM
      return `"${localDateString.replace(/\//g, "-").replace(",", "")}"`;
    }

    // Replace all UTC timestamps with local timestamps
    return str.replace(regex, (match) => {
      return toLocalTime(match);
    });
  },

  convertUTCToLocalDate(value: string, format = ""): string {
    const displayFormat =
      format || process.env.VUE_APP_DISPLAY_DATE || "MM-DD-YYYY";
    if (!moment(value).isValid()) {
      return value;
    }
    return moment(value).utc().local().format(displayFormat);
  },

  formatPhoneNumber(phoneNumberString) {
    const cleaned = ("" + phoneNumberString).replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return "(" + match[1] + ") " + match[2] + "-" + match[3];
    }
    if (cleaned.length === 12) {
      const match12 = cleaned.match(/^(\d{2})(\d{3})(\d{3})(\d{4})$/);
      if (match12) {
        return (
          "(" +
          match12[1] +
          ") " +
          match12[2] +
          "-" +
          match12[3] +
          "-" +
          match12[4]
        );
      }
    }
    return phoneNumberString;
  },

  FromNow(value: string): string {
    if (!moment(value).isValid()) {
      return value;
    }

    return moment(value).fromNow();
  },

  TitleCase(value: string, separator = " ") {
    if (!value) return value;
    const words = value.split(separator);
    words.forEach((word, index) => {
      words[index] = FormatMethods.SentenceCase(word);
    });

    return words.join(" ");
  },

  SentenceCase(value: string) {
    return value.charAt(0).toUpperCase() + value.slice(1);
  },

  KebabCase(value: string): string {
    return value.split(" ").join("-");
  },

  BooleanLabel(
    value: boolean,
    trueLabel = "True",
    falseLabel = "False"
  ): string {
    return value ? trueLabel : falseLabel;
  },

  BooleanYesNo(value: boolean): string {
    return FormatMethods.BooleanLabel(value, "Yes", "No");
  },

  BooleanYesNoSimple(value: boolean): string {
    return FormatMethods.BooleanLabel(value, "Y", "N");
  },

  CamelCaseToWords(value: string): string {
    return value.replace(/([A-Z])/g, " $1");
  },

  SnakeCaseToWords(value: string): string {
    return value.replace(/(_)/g, " ");
  },

  VariableNameToWords(value: string): string {
    return FormatMethods.SnakeCaseToWords(
      FormatMethods.CamelCaseToWords(value)
    );
  },

  DecimalPlaces(value: number | string, scale: number): string {
    return Number(+value).toFixed(scale);
  },

  ConvertDecimalDegreeToDMS(decimalDegrees: number, longOrLat: string) {
    if (typeof decimalDegrees !== "number") {
      return decimalDegrees;
    }

    const hemisphere = /^[WE]|(?:lon)/i.test(longOrLat)
      ? decimalDegrees < 0
        ? "W"
        : "E"
      : decimalDegrees < 0
      ? "S"
      : "N";

    const absDecimalDegrees = Math.abs(decimalDegrees);
    const degrees = truncate(absDecimalDegrees);
    const minutes = truncate((absDecimalDegrees - degrees) * 60);
    const seconds = (
      (absDecimalDegrees - degrees - minutes / 60) *
      Math.pow(60, 2)
    ).toFixed(2);

    const dmsArray = [degrees, minutes, seconds, hemisphere];
    return `${dmsArray[0]}°${dmsArray[1]}'${dmsArray[2]}" ${dmsArray[3]}`;
  },

  convertToLocalTimeZone(date: string, format?: string) {
    if (!format) {
      format = "YYYY-MM-DDTHH:mm:ss";
    }
    return moment
      .utc(date, format)
      .clone()
      .tz(moment.tz.guess())
      .format(format);
  },

  convertPayloadToUTC(date) {
    const formattedDate = new Date(date);
    const year = formattedDate.getUTCFullYear();
    const month = String(formattedDate.getUTCMonth() + 1).padStart(2, "0");
    const day = String(formattedDate.getUTCDate()).padStart(2, "0");
    // Check if the time is already 23:59:59Z
    if (
      formattedDate.getUTCHours() === 23 &&
      formattedDate.getUTCMinutes() === 59 &&
      formattedDate.getUTCSeconds() === 59
    ) {
      return `${year}-${month}-${day}T23:59:59Z`;
    }
    // If not, get the current UTC time
    const currentDate = new Date();
    const hours = String(currentDate.getUTCHours()).padStart(2, "0");
    const minutes = String(currentDate.getUTCMinutes()).padStart(2, "0");
    const seconds = String(currentDate.getUTCSeconds()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
  },

  ParseStringBoolean(value: string | boolean, defaultValue = value) {
    if (typeof value !== "string") {
      return value;
    }
    return ["true", "false"].includes(value) ? value === "true" : defaultValue;
  },

  ParseStringNumber(value: string | number | null) {
    if (typeof value === "number" || value === null) {
      return value;
    }
    const valueNum = parseInt(value, 10);
    if (valueNum.toString() === value) {
      return valueNum;
    }
    return value;
  },
  Ordinal(value: number) {
    if (isNaN(value)) {
      return "";
    }

    const tens = value % 10;
    const hunds = value % 100;

    if (tens === 1 && hunds !== 11) {
      return `st`;
    }

    if (tens === 2 && hunds !== 12) {
      return `nd`;
    }

    if (tens === 3 && hunds !== 13) {
      return `rd`;
    }

    return `th`;
  },

  OrdinalSuffix(value: number, tag = "span") {
    return `${value}${this.Ordinal(value)}`;
  },

  yesNoToIcon(item: boolean) {
    return item
      ? `<a class="btn btn-icon btn-success btn-circle btn-sm mx-auto"><i class="fas fa-check"></i></a>`
      : `<a class="btn btn-icon btn-danger btn-circle btn-sm mx-auto"><i class="fas fa-times"></i></a>`;
  },

  yesNoToIconPlain(item: boolean) {
    return item
      ? `<i class="fas fa-check text-primary"></i>`
      : `<i class="fas fa-times text-danger"></i>`;
  },
  upDownIndicator(text: number | string, cssClass: string) {
    const displayNumber = FormatMethods.DecimalPlaces(text, 2);
    if (text < 0) {
      return `<span style="width:75px;" class="badge ${cssClass}"
      >${+displayNumber * -1} %</span>`;
    }
    if (text > 0) {
      return `<span style="width:75px;" class="badge ${cssClass}"
      >${+displayNumber} %</span>`;
    }

    return `<span style="width:75px;" class="badge ${cssClass}">${+displayNumber} %</span>`;
  },
};

export function truncate(n: number) {
  return n > 0 ? Math.floor(n) : Math.ceil(n);
}

const t = (key: string) => {
  return TranslationService.translate(key);
};

export const getMaxLengthErrorMessage = (fieldLabel, maxLength) => {
  const mustNotExceed = t("common.mustNotExceed");
  const characters = t("common.characters");
  return `${fieldLabel} ${mustNotExceed} ${maxLength} ${characters}`;
};

export const getTimeZoneList = () => {
  const now = moment();

  // Type-safe access (avoids compile error if TS doesn't know about DisplayNames)
  const hasDisplayNames = typeof (Intl as any).DisplayNames === "function";
  const regionNames = hasDisplayNames
    ? new (Intl as any).DisplayNames(["en"], { type: "region" })
    : null;

  const timeZoneList = moment.tz.names().map((name) => {
    const tz = moment.tz(now, name);
    const offset = tz.format("Z");
    const offsetMinutes = tz.utcOffset();

    const parts = name.split("/");
    const city = parts[1]?.replace(/_/g, " ") || "";
    const regionCode = parts[0];

    // Try to get readable region name
    let countryName = regionCode;
    if (regionNames) {
      try {
        countryName = regionNames.of(regionCode.toUpperCase()) || regionCode;
      } catch {
        countryName = regionCode;
      }
    }

    const label = `(GMT${offset}) ${city}${city ? "," : ""} ${countryName}`;

    return {
      label,
      value: name,
      offset,
      offsetMinutes,
    };
  });

  // Sort by UTC offset only
  return timeZoneList
    .sort((a, b) => a.offsetMinutes - b.offsetMinutes)
    .map(({ label, value, offset }) => ({ label, value, offset }));
};
